/**
 * 「対応不要」を表す status 文字列。`opsBqStatus` が `closed` かつ `point === 0`（実作業なし）に対して返す値。
 *
 * **完全一致でこの状態を判定するコードはこの定数を参照する**（例: MCP の AI 推定スキップ判定）。一方
 * シート由来の自由記述 status は「対応不要 🙅🏻‍♀️」のように装飾が付くため、`TERMINAL_STATUS_PATTERNS` は
 * この値を**部分一致のパターンとして**使う。`operation-point-estimator` は SQL 側で `%完了%` /
 * `%バグチケット%` の allowlist で除外しており、この定数は参照しない（値を変えるならそちらも確認すること）。
 * @graph-connects none
 */
export const OPS_BQ_STATUS_NO_ACTION = '対応不要';
/**
 * 終端（クローズ扱い）ステータスの部分一致パターン。対応完了 / 対応不要 / 対応不可 / バグチケット /
 * 原因不明 に加え、「今後も対応する予定がある」も**オペ待ち行列から外す運用**のためクローズ扱いにする
 * （常時対応 = アクティブな未対応ではない、という ops owner 判断）。
 * @graph-connects none
 */
export const TERMINAL_STATUS_PATTERNS = [
    '対応完了',
    OPS_BQ_STATUS_NO_ACTION,
    '対応不可',
    'バグチケット',
    '原因不明',
    '今後も対応',
];
/** 進行が止まっている（依頼者待ち）ステータスの部分一致パターン。 @graph-connects none */
export const BLOCKED_STATUS_PATTERNS = ['差し戻し', 'リトライ', '再確認'];
/**
 * status の先頭（最初の改行・全角/半角括弧より前）を返す。旧ドロップダウンは補助文に終端語を
 * 含むため、分類は先頭のみで行う。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business status セルの先頭トークン（補助文を除いた本体）を取り出す
 * @graph-connects none
 */
export function statusHead(status) {
    return status.split(/[\n（(]/)[0] ?? status;
}
/**
 * ステータスが終端（対応済み・移行済み）かどうか。空・null は open 扱いなので false。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business status が終端（クローズ相当）かどうかを部分一致で判定する
 * @graph-connects none
 */
export function isTerminalStatus(status) {
    if (status === null || status === undefined || status === '')
        return false;
    const head = statusHead(status);
    return TERMINAL_STATUS_PATTERNS.some((p) => head.includes(p));
}
/**
 * フリーテキスト status（+ 担当有無）を進捗ステートへ分類する。product-api の SQL CASE と同じ規則:
 * 終端 → closed / 差戻し → blocked / status か assignee あり → investigating / それ以外 → open。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business status と担当有無から依頼の進捗ステートを導出する（SQL の CASE と同一定義）
 * @graph-connects none
 */
export function classifyState(status, hasAssignee) {
    const s = status ?? '';
    const head = statusHead(s);
    if (s !== '' && TERMINAL_STATUS_PATTERNS.some((p) => head.includes(p)))
        return 'closed';
    if (s !== '' && BLOCKED_STATUS_PATTERNS.some((p) => head.includes(p)))
        return 'blocked';
    if (s.trim() !== '' || hasAssignee)
        return 'investigating';
    return 'open';
}
/**
 * status が「バグチケット移行」（＝バグと確定した終端）かどうか。バグ転換率など bug-vs-not の
 * 集計に使う。status 先頭で 'バグチケット' を部分一致判定する。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business status がバグチケット移行の終端かどうかを判定する（バグ転換の集計に使用）
 * @graph-connects none
 */
export function isBugTicketStatus(status) {
    if (status === null || status === undefined || status === '')
        return false;
    return statusHead(status).includes('バグチケット');
}
/**
 * native 依頼の進捗ステート（+ point / classification）を BQ append 行の status フリーテキストへ写像する。
 * `classifyState` の**逆写像**であり、VIEW / estimator は status からステート・見積もり対象を再判定するため、
 * round-trip で元のステートに戻り、かつ estimator の対象選別（`%完了%` / `%バグチケット%`）と整合する語を選ぶ:
 * - closed かつ bug 確定 → `バグチケット`（バグ転換に計上・未 point なら estimator 対象）
 * - closed かつ point 0（対応不要）→ `対応不要`（終端だが `%完了%` でないため estimator 対象外＝工数 0 を見積もらない）
 * - closed それ以外 → `対応完了`（未 point の closed は estimator が拾う＝AI 見積もりの入口）
 * - blocked → `差し戻し` / investigating → `対応中` / open → `''`（空。担当ありは classifyState 上 investigating へ寄る）
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business native 依頼の進捗ステート(+point/classification)を BQ append 行の status フリーテキストへ写像する（classifyState の逆写像。VIEW/estimator が status から状態・見積もり対象を再判定できる語を選ぶ）
 * @graph-connects none
 */
export function opsBqStatus(input) {
    if (input.state === 'closed') {
        // bug 転換は point より優先（バグチケット化は終端の性質そのもので、バグ計上に載せる）。point 0 との
        // 併存時も `バグチケット`。手動 point 0 は VIEW 側で pointSource='manual' → confirmed_points で
        // estimator 除外されるため、この優先で AI が上書きすることはない。
        if (input.classification === 'bug')
            return 'バグチケット';
        if (input.point === 0)
            return OPS_BQ_STATUS_NO_ACTION;
        return '対応完了';
    }
    if (input.state === 'blocked')
        return '差し戻し';
    if (input.state === 'investigating')
        return '対応中';
    return '';
}
/**
 * フリーテキストの urgency を OpsUrgency へ写像する。即時 / 当日 は緊急扱い。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business urgency のフリーテキストを 即時 / 当日 / 通常 の列挙へ正規化する
 * @graph-connects none
 */
export function mapUrgency(raw) {
    const s = raw ?? '';
    if (s.includes('即時'))
        return '即時';
    if (s.includes('当日') || s.includes('緊急'))
        return '当日';
    return '通常';
}
/**
 * 緊急度が「緊急」（即時 / 当日）か。SLA 目標日の算出（sla.ts）と起票フォームの緊急度ガイド強調
 * （product-innovation-lab）が同じ「緊急扱い」を参照するための単一 source。OpsUrgency に緊急レベルが
 * 増えてもここ 1 箇所を直せばよい。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business 緊急度が緊急（即時 / 当日）かを判定する共有述語。SLA 目標算出と起票フォームの緊急ガイドが同じ「緊急扱い」を参照する単一 source
 * @graph-connects none
 */
export function isUrgentUrgency(urgency) {
    return urgency === '即時' || urgency === '当日';
}
/**
 * 人が選べる依頼種別（起票フォームと詳細の種別変更が共有する選択肢）。`Fatal` は障害検知から自動で
 * 付く種別で人手では選ばせないため除く。zod の `z.enum` に渡せるようリテラルタプルで持つ。
 * @graph-connects none
 */
export const OPS_SELECTABLE_CATEGORIES = [
    '調査依頼',
    '状態変更',
    'データ修正',
    'SQLレビュー',
];
/**
 * フリーテキストの request_category を OpsCategory へ写像する。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business request_category のフリーテキストを 調査依頼 / 状態変更 / データ修正 / SQLレビュー / Fatal の列挙へ正規化する
 * @graph-connects none
 */
export function mapCategory(raw) {
    const s = raw ?? '';
    if (s.includes('状態変更'))
        return '状態変更';
    if (s.includes('データ'))
        return 'データ修正';
    if (s.includes('SQL') || s.includes('ＳＱＬ'))
        return 'SQLレビュー';
    if (s.toLowerCase().includes('fatal') || s.includes('致命'))
        return 'Fatal';
    return '調査依頼';
}
//# sourceMappingURL=status.js.map